import { ReactElement, useContext, useEffect } from 'react';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import LazyHydrate from 'react-lazy-hydration';

import { useVwoTest } from '@/common/abtest/useVwoTest';
import { DrawerContextProvider } from '@/common/components/Drawer/DrawerContext';
import { HeadMetaTags } from '@/common/components/HeadMetaTags/HeadMetaTags';
import PromoBar from '@/common/components/PromoBar/PromoBar';
import RecentlyViewedSection from '@/common/components/RecentlyViewed/RecentlyViewedSection';
import { UserAgentContext } from '@/common/contexts/UserAgentContext/UserAgentContext';
import { useTracking } from '@/common/hooks/useTracking/useTracking';
import DefaultLayoutStack from '@/common/layouts/DefaultLayoutStack';
import { UserAgentPageProps } from '@/common/server/pagesSsrCommon';
import { GitInfo } from '@/common/utils/git/GitInfo';
import { AuthContext } from '@/contexts/auth/AuthContext';
import BrandOfTheWeek from '@/modules/HomePage/components/BrandOfTheWeek/BrandOfTheWeek';
import ComingSoon from '@/modules/HomePage/components/ComingSoon/ComingSoon';
import FeaturedCollections from '@/modules/HomePage/components/FeaturedCollections/FeaturedCollections';
import HPMasonryFeed from '@/modules/HomePage/components/HPMasonryFeed/HPMasonryFeed';
import WhatsNew from '@/modules/HomePage/components/WhatsNew/WhatsNew';
import { HomepageConfig, HpHeaderHeights } from '@/modules/HomePage/HomePageConfig';
import { HomepageContextProvider } from '@/modules/HomePage/HomePageContext';
import { MainLayout } from '@/modules/MainLayout/MainLayout';
import { Newsletter } from '@/modules/Newsletter/Newsletter';
import FooterSection from '@/modules/ProductPage/components/Footer/FooterSection';
import { HomepageV4 } from 'generatedBfw/static';

import Brandshops from './components/Brandshops/Brandshops';
import Categories from './components/Categories/Categories';
import Referrals from './components/Referrals/Referrals';
import SeoLinkingBox from './components/SeoLinkingBox/SeoLinkingBox';
import styles from './homePage.module.scss';

const MobileComingSoonDrawer = dynamic(
  () => import('@/modules/HomePage/components/ComingSoon/ComingSoonDrawer/ComingSoonDrawer'),
);

export interface HomepageProps extends UserAgentPageProps {
  homepageData: HomepageV4;
  gitInfo: GitInfo | null;
}

export const HomePage = ({ isMobile, isTablet, homepageData }: HomepageProps) => {
  const { track } = useTracking();
  const { userInfo } = useContext(AuthContext);
  const cona2623 = useVwoTest('cona2623');

  useEffect(() => {
    track({
      pageCategory: HomepageConfig.analyticsGaName,
      athenaContext: { name: HomepageConfig.analyticsAthenaName, impressionId: null },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <HeadMetaTags pageMeta={HomepageConfig.defaultMeta} />
      <HomepageContextProvider value={homepageData}>
        <DrawerContextProvider
          value={{
            scrollBarWidth: 0,
          }}
        >
          <MainLayout
            defaultHeaderHeights={HpHeaderHeights}
            isMobileOrTablet={isMobile || isTablet}
            topBarData={homepageData.topBar}
            useH1
          >
            <UserAgentContext.Provider
              value={{
                isMobile,
                isTablet,
              }}
            >
              <PromoBar promoBarData={homepageData.promoBar} />
              <div className={clsx(styles.container, styles.lineSeparator)}>
                <WhatsNew />
              </div>
              {cona2623.variant !== 'v1' && cona2623.variant !== 'v2' ? (
                <div className={clsx(styles.container, styles.lineSeparator)}>
                  <LazyHydrate whenIdle>
                    <ComingSoon />
                  </LazyHydrate>
                </div>
              ) : null}
              {isMobile && <MobileComingSoonDrawer events={homepageData.upcomingEvents} />}
              {!userInfo?.isUserLoggedIn && cona2623.variant !== 'v2' ? (
                <LazyHydrate whenIdle>
                  <div className={styles.container}>
                    <Brandshops />
                  </div>
                </LazyHydrate>
              ) : null}
              <div className={styles.container}>
                <HPMasonryFeed />
              </div>
              {userInfo?.isUserLoggedIn ? (
                <LazyHydrate whenIdle>
                  <div
                    className={clsx(
                      styles.container,
                      styles.brandOfTheWeek,
                      styles.brandOfTheWeekBackground,
                    )}
                  >
                    <BrandOfTheWeek />
                  </div>
                </LazyHydrate>
              ) : null}
              {userInfo?.isUserLoggedIn ? (
                <LazyHydrate whenIdle>
                  <div className={styles.container}>
                    <Brandshops />
                  </div>
                </LazyHydrate>
              ) : null}
              <LazyHydrate whenIdle>
                <div className={styles.container}>
                  <FeaturedCollections />
                </div>
              </LazyHydrate>
              {!userInfo?.isUserLoggedIn ? (
                <LazyHydrate whenIdle>
                  <div className={clsx(styles.container, styles.brandOfTheWeek)}>
                    <BrandOfTheWeek />
                  </div>
                </LazyHydrate>
              ) : null}
              <div className={styles.container}>
                <LazyHydrate whenIdle>
                  <RecentlyViewedSection />
                </LazyHydrate>
                <LazyHydrate whenVisible>
                  <Categories />
                </LazyHydrate>
                {homepageData.trustPilot ? (
                  <LazyHydrate whenIdle>
                    <Referrals trustPilot={homepageData.trustPilot} />
                  </LazyHydrate>
                ) : null}
                <Newsletter />
                <FooterSection />
                <div className={styles.container}>
                  <SeoLinkingBox />
                </div>
              </div>
            </UserAgentContext.Provider>
          </MainLayout>
        </DrawerContextProvider>
      </HomepageContextProvider>
    </>
  );
};

HomePage.getLayout = function getLayout(page: ReactElement) {
  return <DefaultLayoutStack gitInfo={page.props.gitInfo}>{page}</DefaultLayoutStack>;
};
